import type {
  FloorAlarmState,
  FloorplanAlarmLocation,
  FloorplanAlarmRegion,
  FloorplanFloor,
  FloorplanRoom,
} from "./types";

/**
 * Alarm state drawn per region of the floor plan, never over the whole plan.
 *
 * One plan draws several floors (prod: "Floor 4" holds Luntmakargatan 22 and 26,
 * each with its own alarm), so every location keeps its own state (backend
 * `iot.floor_alarm.alarm_regions_for_floorplan`) and only its drawn area is coloured:
 *
 * - a floor's region shows the floor's own state, else its address's;
 * - a room polygon with its own alarm links shows its own state, on top of its floor;
 *   a room without links inherits by lying inside its floor's region;
 * - a location without a region on this plan shows nothing here (never the whole plan);
 * - armed: light red tint + red outline; pre_alarm: light orange tint + pulsing orange
 *   outline (no pulse under `prefers-reduced-motion`); off / unknown: nothing.
 *
 * A legend outside the zoom (bottom-left, clear of the layers panel top-right) names each
 * coloured region, so the label stays put while panning.
 */

const STYLE = `
.nodus-fp-alarm-regions, .nodus-fp-alarm-legend {
  --nodus-fp-alarm-red: var(--mantine-color-red-8, #c92a2a);
  --nodus-fp-alarm-red-tint: rgba(224, 49, 49, 0.16);
  --nodus-fp-alarm-orange: var(--mantine-color-orange-6, #fd7e14);
  --nodus-fp-alarm-orange-tint: rgba(253, 126, 20, 0.14);
  --nodus-fp-alarm-orange-chip: var(--mantine-color-orange-4, #ffa94d);
}
.nodus-fp-alarm-region polygon {
  vector-effect: non-scaling-stroke;
  stroke-linejoin: round;
}
.nodus-fp-alarm-region[data-state="armed"] polygon {
  fill: var(--nodus-fp-alarm-red-tint);
  stroke: var(--nodus-fp-alarm-red);
  stroke-width: 3px;
}
.nodus-fp-alarm-region[data-state="pre_alarm"] polygon {
  fill: var(--nodus-fp-alarm-orange-tint);
  stroke: var(--nodus-fp-alarm-orange);
  stroke-width: 4px;
  animation: nodus-fp-alarm-pulse 1.6s ease-in-out infinite;
}
@keyframes nodus-fp-alarm-pulse {
  0%, 100% { stroke-opacity: 1; }
  50% { stroke-opacity: 0.35; }
}
@media (prefers-reduced-motion: reduce) {
  .nodus-fp-alarm-region[data-state="pre_alarm"] polygon { animation: none; }
}
.nodus-fp-alarm-legend {
  position: absolute;
  left: 12px;
  bottom: 12px;
  z-index: 4;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
  max-width: calc(100% - 24px);
  pointer-events: none;
}
.nodus-fp-alarm-chip {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  max-width: 100%;
  box-sizing: border-box;
  padding: 6px 12px;
  border-radius: 999px;
  font: 600 13px/1.3 system-ui, -apple-system, "Segoe UI", sans-serif;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.25);
  text-align: left;
}
.nodus-fp-alarm-chip svg { flex: none; }
.nodus-fp-alarm-chip[data-state="armed"] { background: var(--nodus-fp-alarm-red); color: #fff; }
.nodus-fp-alarm-chip[data-state="pre_alarm"] { background: var(--nodus-fp-alarm-orange-chip); color: #241200; }
`;

const DRAWN: ReadonlySet<FloorAlarmState> = new Set(["armed", "pre_alarm"]);

function key(type: string, id: number): string {
  return `${type}:${id}`;
}

/** The backend rows (`alarm_regions` of a floor plan payload) as viewer props. */
export function toAlarmLocations(
  rows: Array<{ location_type: string; location_id: number; state: FloorAlarmState | null; since?: string | null; warning_minutes?: number | null }> | null | undefined,
): FloorplanAlarmLocation[] {
  return (rows ?? [])
    .filter((r) => r.state && (r.location_type === "floor" || r.location_type === "address" || r.location_type === "room"))
    .map((r) => ({
      locationType: r.location_type as FloorplanAlarmLocation["locationType"],
      locationId: r.location_id,
      state: r.state as FloorAlarmState,
      since: r.since ?? null,
      warningMinutes: r.warning_minutes ?? null,
    }));
}

/** The regions to colour: see the module comment for the rule. Floors first, rooms on top. */
export function resolveAlarmRegions(
  floors: FloorplanFloor[] | null | undefined,
  rooms: FloorplanRoom[] | null | undefined,
  locations: FloorplanAlarmLocation[] | null | undefined,
): FloorplanAlarmRegion[] {
  if (!locations?.length) return [];
  const byKey = new Map(locations.map((l) => [key(l.locationType, l.locationId), l]));
  const regions: FloorplanAlarmRegion[] = [];
  for (const f of floors ?? []) {
    if (!f.region || f.region.length < 3) continue;
    const own = byKey.get(key("floor", f.floorId));
    const viaAddress = f.addressId != null ? byKey.get(key("address", f.addressId)) : undefined;
    const alarm = own ?? viaAddress;
    if (!alarm || !DRAWN.has(alarm.state)) continue;
    regions.push({
      key: key("floor", f.floorId),
      locationType: "floor",
      locationId: f.floorId,
      state: alarm.state as FloorplanAlarmRegion["state"],
      label: [f.addressName, f.floorName].filter(Boolean).join(" "),
      polygons: [f.region],
      inheritedFrom: own ? undefined : "address",
    });
  }
  for (const r of rooms ?? []) {
    const own = byKey.get(key("room", r.id));
    const rings = (r.polygon ?? []).filter((ring) => ring && ring.length >= 3);
    if (!own || !DRAWN.has(own.state) || rings.length === 0) continue;
    regions.push({
      key: key("room", r.id),
      locationType: "room",
      locationId: r.id,
      state: own.state as FloorplanAlarmRegion["state"],
      label: r.name,
      polygons: rings,
    });
  }
  return regions;
}

function points(ring: number[][]): string {
  return ring.map((p) => `${p[0]},${p[1]}`).join(" ");
}

/** Inside the plan's <svg>, in its coordinates: one tinted outline per alarmed region. */
export function FloorplanAlarmRegions({ regions }: { regions: FloorplanAlarmRegion[] }) {
  if (regions.length === 0) return null;
  return (
    <g className="nodus-fp-alarm-regions" pointerEvents="none" data-testid="floorplan-alarm-regions">
      <style>{STYLE}</style>
      {regions.map((r) => (
        <g key={r.key} className="nodus-fp-alarm-region" data-state={r.state} data-location={r.key} data-testid="floorplan-alarm-region">
          {r.polygons.map((ring, i) => (
            <polygon key={i} points={points(ring)} />
          ))}
        </g>
      ))}
    </g>
  );
}

function LockIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="4" y="11" width="16" height="10" rx="2" />
      <path d="M8 11V7a4 4 0 0 1 8 0v4" />
    </svg>
  );
}

function ClockIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </svg>
  );
}

export function alarmRegionText(region: Pick<FloorplanAlarmRegion, "state" | "label">): string {
  return region.state === "armed"
    ? `Alarm is on: ${region.label}`
    : `Alarm turns on soon: ${region.label}. Use your tag to keep it off.`;
}

/** Outside the zoom transform: one chip per coloured region, so it stays readable while panning. */
export function FloorplanAlarmLegend({ regions }: { regions: FloorplanAlarmRegion[] }) {
  if (regions.length === 0) return null;
  return (
    <div className="nodus-fp-alarm-legend" data-testid="floorplan-alarm-legend" role="status" aria-live="polite">
      <style>{STYLE}</style>
      {regions.map((r) => (
        <div key={r.key} className="nodus-fp-alarm-chip" data-state={r.state}>
          {r.state === "armed" ? <LockIcon /> : <ClockIcon />}
          <span>{alarmRegionText(r)}</span>
        </div>
      ))}
    </div>
  );
}
